import { GAME_MODES, type PlayerInfo } from '@blind-kings/game-core';
import { api } from 'aws-blocks';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../contexts/AuthContext';
import useApiAction from '../../../hooks/use-api-action';
import { playerIsInGameId } from '../../../utils/active-games';

export type GameInfoPlayer = PlayerInfo & {
  ready?: boolean;
};

export type UseGameInfoOptions = {
  game: {
    gameId?: string;
    gameMode?: string;
    firstPlayer?: GameInfoPlayer;
    secondPlayer?: GameInfoPlayer;
  };
  applyGameUpdate?: (payload: Record<string, unknown>) => void;
};

export default function useGameInfo({
  game: { gameId, gameMode, firstPlayer, secondPlayer },
  applyGameUpdate,
}: UseGameInfoOptions) {
  const navigate = useNavigate();
  const { userData } = useAuthContext();
  const { playerId } = userData ?? {};

  const firstPlayerId = firstPlayer?.playerId;
  const firstPlayerReady = firstPlayer?.ready;
  const secondPlayerId = secondPlayer?.playerId;
  const secondPlayerReady = secondPlayer?.ready;

  const [joinGame, joiningGame] = useApiAction({
    action: async () => api.joinGame(String(gameId)),
    reloadUserData: true,
  });

  const [leaveGame, leavingGame] = useApiAction({
    action: async () => api.leaveGame(String(gameId)),
    reloadUserData: true,
  });

  const [getReady, gettingReady] = useApiAction({
    action: async () => api.getReady(String(gameId)),
  });

  const [startGame, startingGame] = useApiAction({
    action: async () => api.startGame(String(gameId)),
  });

  const isFirstPlayer = playerId === firstPlayerId;
  const isSecondPlayer = playerId === secondPlayerId;
  const isLocalGame = gameMode === GAME_MODES.LOCAL_GAME;
  const isInGame = playerIsInGameId(userData, String(gameId));

  const isReady = isFirstPlayer
    ? firstPlayerReady
    : isSecondPlayer
      ? secondPlayerReady
      : false;

  const canStart = isFirstPlayer && firstPlayerReady && secondPlayerReady;
  const canJoin = !isInGame && !secondPlayerId;
  const canBeReady = isInGame && firstPlayerId && secondPlayerId && !isReady;
  const actionButtonDisabled =
    (!canBeReady && !canStart && !canJoin) || joiningGame || leavingGame;

  const actionButtonText = joiningGame
    ? 'Joining...'
    : gettingReady
      ? 'Getting Ready...'
      : startingGame
        ? 'Starting Game...'
        : isInGame
          ? isReady
            ? 'Start'
            : 'Ready'
          : 'Join';

  async function handleActionButtonOnClick() {
    if (canJoin) {
      const joined = (await joinGame()) as Record<string, unknown> | undefined;
      if (joined) applyGameUpdate?.(joined);
      return joined;
    }
    if (!isReady) {
      const next = (await getReady()) as Record<string, unknown> | undefined;
      if (next) applyGameUpdate?.(next);
      return next;
    }
    if (canStart) {
      const next = (await startGame()) as Record<string, unknown> | undefined;
      if (next) applyGameUpdate?.(next);
      return next;
    }
  }

  async function handleLeave() {
    try {
      await leaveGame();
      navigate('/');
    } catch (err) {
      console.error(err);
    }
  }

  return {
    isInGame,
    isLocalGame,
    leavingGame,
    actionButtonDisabled,
    actionButtonText,
    isReady,
    handleActionButtonOnClick,
    handleLeave,
  };
}
